import React, { useMemo, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, ChevronLeftIcon, DateInput, SearchIcon } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, ToolbarField, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import { accountingReturnState } from '../shared/navigation';

type Line = {
  id: number | null;
  documentId: number | null;
  date: string | null;
  docCode: string | null;
  narrative: string;
  debitLocal: number;
  creditLocal: number;
  localBalance: number;
  debitUSD: number;
  creditUSD: number;
  usdBalance: number;
  isBroughtForward: boolean;
};

type Response = {
  companyId: number;
  linkNumber: string;
  companyCode: string;
  companyName: string;
  code: string;
  combLevel: string;
  name: string;
  dateFrom: string;
  dateTo: string;
  rows: Line[];
};

const formatKartelaAmount = (value: number) => (value === 0 ? '' : formatReportAmount(value));

export const LinkStatement: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get('companyId') ?? '';
  const accountId = searchParams.get('accountId') ?? '';
  const linkNumber = searchParams.get('linkNumber') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '2000-01-01';
  const dateTo = searchParams.get('dateTo') ?? new Date().toISOString().slice(0, 10);

  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [run, setRun] = useState(Boolean(companyId && accountId && linkNumber));

  const reportUrl =
    run && companyId && accountId && linkNumber
      ? buildReportQuery('/accounting/reports/link-statement', {
          companyId,
          accountId,
          linkNumber,
          dateFrom,
          dateTo,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const returnState = useMemo(
    () => accountingReturnState(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const columns = useMemo<Column<Line>[]>(
    () => [
      { key: 'date', header: 'Date', render: (v) => (v ? formatDateGlobal(v) : '—') },
      {
        key: 'docCode',
        header: 'Doc #',
        render: (v, row) =>
          row.documentId && v ? (
            <Link
              to={`/accounting/journals/${row.documentId}/edit`}
              state={returnState}
              className="text-indigo-600 hover:underline"
            >
              {v}
            </Link>
          ) : (
            v ?? '—'
          ),
      },
      { key: 'narrative', header: 'Details' },
      { key: 'debitLocal', header: 'Debit', align: 'right', render: formatKartelaAmount },
      { key: 'creditLocal', header: 'Credit', align: 'right', render: formatKartelaAmount },
      { key: 'localBalance', header: 'Balance', align: 'right', render: (v: number) => formatReportAmount(v) },
      { key: 'debitUSD', header: 'Debit USD', align: 'right', render: formatKartelaAmount },
      { key: 'creditUSD', header: 'Credit USD', align: 'right', render: formatKartelaAmount },
      { key: 'usdBalance', header: 'Bal USD', align: 'right', render: (v: number) => formatReportAmount(v) },
    ],
    [returnState],
  );

  const handleSearch = () => {
    const next = new URLSearchParams(searchParams);
    next.set('dateFrom', draftFrom);
    next.set('dateTo', draftTo);
    setSearchParams(next);
    setRun(true);
  };

  const goBack = useNavigateBack('/accounting/reports/open-items');

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Button variant="ghost" size="sm" onClick={goBack} className="gap-1 shrink-0">
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </Button>
        <Badge variant="secondary">{data?.companyName ?? data?.companyCode ?? companyId}</Badge>
        <Badge variant="secondary">{data?.combLevel ?? ''} {data?.name ?? ''}</Badge>
        <Badge variant="info">Link {linkNumber}</Badge>
      </div>
      <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-3 pt-3 border-t', colors.border)}>
        <ToolbarField label="From">
          <DateInput value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} showFormatHint={false} />
        </ToolbarField>
        <ToolbarField label="To">
          <DateInput value={draftTo} onChange={(e) => setDraftTo(e.target.value)} showFormatHint={false} />
        </ToolbarField>
        <Button variant="default" onClick={handleSearch} disabled={!companyId || !accountId || !linkNumber} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  if (!companyId || !accountId || !linkNumber) {
    return (
      <div className={cn('p-6', colors.textMuted)}>
        Missing company, account, or link number in URL.
      </div>
    );
  }

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Link Statement"
      exportFileBase="link-statement"
      exportDateTo={dateTo}
      statusBadge={
        <Badge variant="info" className="font-normal">
          {formatDateGlobal(dateFrom)} – {formatDateGlobal(dateTo)}
        </Badge>
      }
      recordCount={rows.filter((r) => !r.isBroughtForward).length}
      readyMessage="Adjust the date range and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.id ?? `bf-${r.documentId}-${r.date}`)}
        emptyMessage="No lines for this link."
        stickyHeader
      />
    </AccountingReportShell>
  );
};
